import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

// ── i18n ────────────────────────────────────────────────────────────────────
export const DICT = {
  fallbackTitle: { en: "Daily Trend Report", zh: "每日趨勢報告" },
  subtitle: {
    en: "Thermal industry signals for T-Global, refreshed daily",
    zh: "為 T-Global 每日更新的散熱產業情報",
  },
  items: { en: "items", zh: "則新聞" },
  topicsWithNews: { en: "topics with news", zh: "個主題有新聞" },
  applicationsWithNews: {
    en: "applications with news",
    zh: "個應用領域有新聞",
  },
  whyItMatters: { en: "Why it matters", zh: "對 T-Global 的意義" },
  // Named for its author, not its content: this section is the model's own
  // synthesis, and saying so keeps the reader from reading it with the same
  // authority as the sourced stories below.
  aiSynthesis: { en: "AI Synthesis & Analysis", zh: "AI 綜合分析" },
  readSource: { en: "Read source", zh: "閱讀來源" },
  citedAs: { en: "Cited as", zh: "引用為" },
  needsReview: { en: "Needs review", zh: "待確認" },
  openInNewTab: { en: "Open in new tab", zh: "在新分頁開啟" },
  embedHint: {
    en: "Some sites block embedding — use “Open in new tab” if this looks blank.",
    zh: "部分網站不允許嵌入顯示，若畫面空白請點選「在新分頁開啟」。",
  },
  allNews: { en: "All news gathered", zh: "所有蒐集的新聞" },
  viewAllReports: { en: "Browse past reports", zh: "瀏覽過往報告" },
  clickToExpand: { en: "Click to expand", zh: "點擊展開" },
  clickToCollapse: { en: "Click to collapse", zh: "點擊收合" },
  loading: { en: "Loading trend report…", zh: "正在載入趨勢報告…" },
  empty: {
    en: "No trend report has been generated yet.",
    zh: "尚未產生任何趨勢報告。",
  },
  error: { en: "Could not load the trend report.", zh: "無法載入趨勢報告。" },
  pageTitle: { en: "Trend Reports", zh: "趨勢報告" },
  pastReports: { en: "Past reports", zh: "過往報告" },
  browseSubtitle: {
    en: "Browse every daily briefing we've generated",
    zh: "瀏覽我們產生過的每一份每日簡報",
  },
  noReportsYet: { en: "No reports yet.", zh: "尚無任何報告。" },
  noReportsInRange: {
    en: "No reports in this date range.",
    zh: "此日期範圍內沒有報告。",
  },
  badDateRange: {
    en: "The start date is after the end date.",
    zh: "開始日期晚於結束日期。",
  },
  timeRange: { en: "Time range", zh: "時間範圍" },
  allDates: { en: "All dates", zh: "所有日期" },
  quickRanges: { en: "Quick ranges", zh: "快速選擇" },
  pickEndDate: { en: "pick end date", zh: "請選擇結束日期" },
  clickStartEnd: {
    en: "Click a start and end date",
    zh: "請點選開始與結束日期",
  },
  clearDates: { en: "Clear", zh: "清除" },
  listError: { en: "Could not load report list.", zh: "無法載入報告清單。" },
  latest: { en: "Latest", zh: "最新" },
  readReport: { en: "Read report", zh: "閱讀報告" },
  backToReports: { en: "All reports", zh: "所有報告" },
  reportNotFound: {
    en: "That report could not be found.",
    zh: "找不到該份報告。",
  },
  priorityUploadTitle: {
    en: "Priority news for the next report",
    zh: "下一份報告的優先新聞",
  },
  priorityUploadHint: {
    en: "Queued items are guaranteed to appear in tomorrow's report, regardless of relevance score.",
    zh: "已加入的項目無論相關性分數為何，都保證出現在明日的報告中。",
  },
  priorityTitleLabel: { en: "Title (optional)", zh: "標題（選填）" },
  priorityNotesLabel: {
    en: "Notes / main idea (optional if a PDF is attached)",
    zh: "備註／主要內容（若已附上 PDF 則可選填）",
  },
  priorityPdfLabel: { en: "Attach PDF (optional)", zh: "附上 PDF（選填）" },
  priorityQueueButton: { en: "Queue for tomorrow", zh: "加入明日報告佇列" },
  priorityQueuing: { en: "Queuing…", zh: "加入中…" },
  priorityQueuedItems: { en: "Queued for", zh: "已排入" },
  priorityNoneQueued: {
    en: "Nothing queued yet.",
    zh: "目前尚未排入任何項目。",
  },
  priorityRemove: { en: "Remove", zh: "移除" },
  priorityUploadError: {
    en: "Could not queue that item.",
    zh: "無法加入該項目。",
  },
  // ── daily email report ────────────────────────────────────────────────────
  dailyEmailTab: { en: "Daily Email Report", zh: "每日郵件報告" },
  newsletterTitle: { en: "Daily email report", zh: "每日郵件報告" },
  newsletterHint: {
    en: "Once each day's report is generated it is emailed via Gmail to everyone on this list.",
    zh: "每日報告產生後，會透過 Gmail 寄送給清單上的所有人。",
  },
  newsletterRecipients: { en: "Recipients", zh: "收件人" },
  newsletterAddEmail: { en: "name@company.com", zh: "name@company.com" },
  newsletterAddName: { en: "Name (optional)", zh: "姓名（選填）" },
  newsletterAdd: { en: "Add", zh: "新增" },
  newsletterAdding: { en: "Adding…", zh: "新增中…" },
  newsletterPickUsers: { en: "Add from app users", zh: "從應用程式使用者新增" },
  newsletterPickUsersDenied: {
    en: "You do not have permission to browse app users — add addresses by hand instead.",
    zh: "您沒有瀏覽應用程式使用者的權限，請改為手動輸入電子郵件。",
  },
  newsletterSearchUsers: { en: "Search users…", zh: "搜尋使用者…" },
  newsletterExternal: { en: "External", zh: "外部" },
  newsletterUnsubscribed: { en: "Unsubscribed", zh: "已取消訂閱" },
  newsletterNoRecipients: { en: "No recipients yet.", zh: "尚無收件人。" },
  newsletterAddError: {
    en: "Could not add that recipient.",
    zh: "無法新增該收件人。",
  },
  newsletterSendTest: { en: "Send test to me", zh: "寄送測試給我" },
  newsletterSendNow: { en: "Send today's brief now", zh: "立即寄送今日簡報" },
  newsletterSending: { en: "Sending…", zh: "寄送中…" },
  newsletterSendConfirm: {
    en: "Email today's report to every active recipient now?",
    zh: "立即將今日報告寄送給所有有效收件人？",
  },
  newsletterTestSent: { en: "Test sent to", zh: "測試郵件已寄至" },
  newsletterSendError: { en: "Could not send.", zh: "寄送失敗。" },
  newsletterHistory: { en: "Delivery history", zh: "寄送紀錄" },
  newsletterLatestActivity: { en: "Latest delivery", zh: "最近一次寄送" },
  newsletterShowEarlier: { en: "Show earlier", zh: "顯示先前紀錄" },
  newsletterHideEarlier: { en: "Hide earlier", zh: "隱藏先前紀錄" },
  newsletterNoHistory: { en: "Nothing sent yet.", zh: "尚未寄送任何郵件。" },
  newsletterTrimmed: { en: "trimmed", zh: "已裁減" },
  newsletterStatusSent: { en: "Sent", zh: "已寄送" },
  newsletterStatusPartial: { en: "Partial", zh: "部分成功" },
  newsletterStatusFailed: { en: "Failed", zh: "失敗" },
  newsletterStatusRunning: { en: "Sending", zh: "寄送中" },
  // Own loading/error copy: the tab used to borrow the page-level `loading` /
  // `error` keys, which say "Loading trend report…" over a recipient list.
  newsletterLoading: { en: "Loading recipients…", zh: "正在載入收件人…" },
  newsletterError: {
    en: "Could not load the recipient list.",
    zh: "無法載入收件人清單。",
  },
  // ── audiences ─────────────────────────────────────────────────────────────
  newsletterAudiences: { en: "Audiences", zh: "寄送對象" },
  newsletterAudienceHint: {
    en: "Pick the user groups that should receive the brief. Membership is re-checked before every send.",
    zh: "選擇應收到簡報的使用者群組，每次寄送前都會重新確認成員。",
  },
  newsletterNoAudiences: {
    en: "No group audiences yet — add one, or add individual recipients below.",
    zh: "尚未設定群組對象 — 請新增群組，或於下方新增個別收件人。",
  },
  newsletterAddAudience: { en: "Add audience", zh: "新增寄送對象" },
  // ── unified audience panel ────────────────────────────────────────────────
  // Groups and the people they resolve to used to live in two stacked cards,
  // which read as two competing lists of the same recipients. They are now one
  // list where members nest under the group that put them there.
  newsletterWhoGets: { en: "Who gets this brief", zh: "誰會收到簡報" },
  newsletterWhoGetsHint: {
    en: "Group membership is re-checked before every send. Expand a group to see exactly who is on the list.",
    zh: "每次寄送前都會重新確認群組成員。展開群組即可查看實際的收件人。",
  },
  newsletterAddPeople: { en: "Add people", zh: "新增收件人" },
  newsletterNoOne: {
    en: "Nobody is on the list yet — add a group or a person to start sending.",
    zh: "清單目前是空的 — 新增群組或個人即可開始寄送。",
  },
  newsletterEmptyGroup: {
    en: "No active members in this group.",
    zh: "此群組目前沒有有效成員。",
  },
  newsletterIndividuals: { en: "Added individually", zh: "個別新增" },
  newsletterIndividualsHint: {
    en: "Pinned — a group sync leaves these alone.",
    zh: "已鎖定 — 群組同步不會變更這些項目。",
  },
  newsletterShowMembers: { en: "Show members", zh: "顯示成員" },
  newsletterHideMembers: { en: "Hide members", zh: "隱藏成員" },
  newsletterRemoveAudience: {
    en: "Remove this group? People in it stop receiving the brief. Anyone who unsubscribed stays unsubscribed.",
    zh: "要移除此群組嗎？群組成員將不再收到簡報。已取消訂閱者仍維持取消狀態。",
  },
  newsletterSyncNow: { en: "Sync now", zh: "立即同步" },
  newsletterSyncing: { en: "Syncing…", zh: "同步中…" },
  newsletterSynced: { en: "Audiences synced", zh: "寄送對象已同步" },
  newsletterSyncError: { en: "Could not sync.", zh: "同步失敗。" },
  newsletterMembers: { en: "members", zh: "位成員" },
  // Picker segments
  newsletterTabGroups: { en: "Groups", zh: "群組" },
  newsletterTabPeople: { en: "People", zh: "使用者" },
  newsletterTabEmail: { en: "Email", zh: "電子郵件" },
  newsletterSearchGroups: { en: "Search groups…", zh: "搜尋群組…" },
  newsletterNoGroups: { en: "No groups found.", zh: "找不到群組。" },
  newsletterNoUsers: { en: "No users found.", zh: "找不到使用者。" },
  newsletterAlreadyAdded: { en: "Already added", zh: "已新增" },
  newsletterPickerHint: {
    en: "Tick whole user groups, people from the directory, or type in an outside address. You choose the language on the next step.",
    zh: "勾選整個使用者群組、目錄中的使用者，或直接輸入外部電子郵件。語言會在下一步選擇。",
  },
  newsletterAdded: { en: "Added", zh: "已新增" },
  newsletterAddedNotice: {
    en: "Added to the list — keep adding, or close when you're done.",
    zh: "已加入清單 — 可繼續新增，完成後請關閉。",
  },
  // ── multi-select picker ───────────────────────────────────────────────────
  // Adding used to be one row, one click, one round trip. Selection is now
  // collected first and committed as a batch, with the language asked once at
  // the point of committing rather than set as a mode beforehand.
  newsletterColName: { en: "Name", zh: "姓名" },
  newsletterColEmail: { en: "Email", zh: "電子郵件" },
  newsletterColGroup: { en: "Group", zh: "群組" },
  newsletterColMembers: { en: "Members", zh: "成員人數" },
  newsletterColStatus: { en: "Status", zh: "狀態" },
  newsletterSelectAll: { en: "Select all", zh: "全選" },
  newsletterSelectRow: { en: "Select", zh: "選取" },
  newsletterSelectedCount: { en: "selected", zh: "已選取" },
  newsletterAddSelected: { en: "Add selected", zh: "新增所選" },
  newsletterAddToSelection: { en: "Add to selection", zh: "加入選取清單" },
  newsletterNothingSelected: {
    en: "Tick the boxes to choose who to add.",
    zh: "請勾選要新增的對象。",
  },
  newsletterClearSelection: { en: "Clear", zh: "清除" },
  newsletterCancel: { en: "Cancel", zh: "取消" },
  newsletterAddedResult: { en: "added", zh: "已新增" },
  newsletterAddFailedCount: { en: "could not be added", zh: "無法新增" },
  newsletterTruncated: {
    en: "Showing the first 60 matches — narrow the search to see the rest.",
    zh: "僅顯示前 60 筆符合結果，請縮小搜尋範圍以查看其餘項目。",
  },
  // Language targeting
  newsletterLangEn: { en: "EN", zh: "英文" },
  newsletterLangZh: { en: "中文", zh: "中文" },
  newsletterLangBoth: { en: "Both", zh: "兩種" },
  newsletterLanguage: { en: "Language", zh: "語言" },
  // The segmented control means nothing on its own, so anywhere it is a
  // primary choice (rather than a tweak to one existing row) it is introduced.
  newsletterLanguageTitle: {
    en: "Newsletter language",
    zh: "電子報語言",
  },
  newsletterLanguageHint: {
    en: "Choose which language edition of the newsletter is sent. “Both” sends two separate emails.",
    zh: "選擇要寄送的電子報語言版本。「兩種」會分別寄出兩封郵件。",
  },
  // The footer asks the same question in the width of a toolbar.
  newsletterLanguageHintShort: {
    en: "“Both” sends two separate emails.",
    zh: "「兩種」會分別寄出兩封郵件。",
  },
  // Caption for the control when it sits inline on a row, where the block form
  // with its paragraph of hint text would not fit.
  newsletterRowLanguage: { en: "Newsletter language", zh: "電子報語言" },
  newsletterMemberLanguageHint: {
    en: "Members receive the group's language. Setting one here pins that person, so a later sync leaves them alone.",
    zh: "成員預設使用群組的語言。在此單獨設定會鎖定該成員，之後同步不會再變更。",
  },
  newsletterLangEnFull: { en: "Send the English edition", zh: "寄送英文版" },
  newsletterLangZhFull: { en: "Send the Chinese edition", zh: "寄送中文版" },
  newsletterLangBothFull: {
    en: "Send both editions — two separate emails",
    zh: "兩種語言都寄送 — 共兩封郵件",
  },
  // Recipient sources
  newsletterSourceManual: { en: "Manual", zh: "手動新增" },
  newsletterOptedOut: { en: "Opted out", zh: "已取消訂閱" },
  newsletterShowOptedOut: { en: "Opted out", zh: "已取消訂閱" },
  newsletterPeopleCount: { en: "people", zh: "人" },
  newsletterEmailsCount: { en: "emails", zh: "封郵件" },
  newsletterClose: { en: "Close", zh: "關閉" },
  // Public unsubscribe / resubscribe pages (public/)
  pubBrand: { en: "T-Global Daily Brief", zh: "T-Global 每日簡報" },
  pubBack: { en: "Back", zh: "返回" },
  pubInvalidTitle: { en: "This link isn't valid", zh: "此連結無效" },
  pubErrorTitle: { en: "Something went wrong", zh: "發生錯誤" },
  pubErrorBody: {
    en: "We couldn't process that just now. Please try again later.",
    zh: "目前無法處理您的請求，請稍後再試。",
  },
  unsubTitle: {
    en: "Unsubscribe from the T-Global Daily Brief",
    zh: "取消訂閱 T-Global 每日簡報",
  },
  unsubIntro: {
    en: "Enter the email address this brief was sent to. We check that it matches before anything changes.",
    zh: "請輸入收到此簡報的電子郵件地址。我們會先確認地址相符，才會進行任何變更。",
  },
  unsubEmailLabel: { en: "Email address", zh: "電子郵件地址" },
  unsubEmailPlaceholder: { en: "name@company.com", zh: "name@company.com" },
  unsubContinue: { en: "Continue", zh: "繼續" },
  unsubChecking: { en: "Checking…", zh: "確認中…" },
  unsubConfirmTitle: { en: "Unsubscribe this address?", zh: "確定要取消訂閱此地址嗎？" },
  unsubConfirmBody: {
    en: "It will stop receiving the T-Global Daily Brief. We'll send it a confirmation with a link to undo this.",
    zh: "此地址將不再收到 T-Global 每日簡報。我們會寄送確認信至此地址，信中附有復原連結。",
  },
  unsubConfirmButton: { en: "Unsubscribe", zh: "取消訂閱" },
  unsubWorking: { en: "Unsubscribing…", zh: "處理中…" },
  unsubMismatchTitle: { en: "This address doesn't match", zh: "此地址不相符" },
  unsubMismatchBody: {
    en: "This copy of the brief wasn't sent to that address, so it can't be used to unsubscribe it. You may have received it from someone else — forwarded to you, or through a shared mailing list — rather than from our recipient list. Please contact whoever sent it to you.",
    zh: "此份簡報並非寄給該地址，因此無法用來取消該地址的訂閱。您可能是透過他人轉寄或共用郵件群組收到此簡報，而非由我們的收件人名單直接寄送。請聯絡寄送給您的人處理。",
  },
  unsubMismatchOwnCopy: {
    en: "If you're subscribed yourself, use the Unsubscribe link in your own copy of the brief.",
    zh: "如果您本人也有訂閱，請使用您自己收到的簡報中的「取消訂閱」連結。",
  },
  unsubTryAgain: { en: "Try a different address", zh: "改用其他地址" },
  unsubDoneTitle: { en: "You're unsubscribed", zh: "已取消訂閱" },
  unsubDoneBody: {
    en: "You won't receive the T-Global Daily Brief any more. We've sent a confirmation to this address — if you didn't make this request, the link in it undoes it.",
    zh: "您將不再收到 T-Global 每日簡報。我們已寄送確認信至此地址；如果這不是您本人的操作，可透過信中的連結復原。",
  },
  unsubInvalidBody: {
    en: "It may be incomplete, or the subscription was removed.",
    zh: "連結可能不完整，或該訂閱已被移除。",
  },
  resubTitle: {
    en: "Resubscribe to the T-Global Daily Brief?",
    zh: "要重新訂閱 T-Global 每日簡報嗎？",
  },
  resubIntro: {
    en: "This undoes the recent unsubscribe for your address, and the brief starts arriving again.",
    zh: "這將復原您地址最近一次的取消訂閱，您將重新開始收到簡報。",
  },
  resubButton: { en: "Resubscribe", zh: "重新訂閱" },
  resubWorking: { en: "Resubscribing…", zh: "處理中…" },
  resubDoneTitle: { en: "You're subscribed again", zh: "已重新訂閱" },
  resubDoneBody: {
    en: "The T-Global Daily Brief will arrive as usual from the next issue.",
    zh: "從下一期開始，您將照常收到 T-Global 每日簡報。",
  },
  resubHeadsUpTitle: {
    en: "If you didn't unsubscribe yourself",
    zh: "如果取消訂閱並非您本人的操作",
  },
  resubHeadsUpBody: {
    en: "Someone used your email address on our unsubscribe page. That usually means they have a copy of a brief that was sent to you — for example, one you forwarded. You may want to check who you've shared it with.",
    zh: "有人在我們的取消訂閱頁面使用了您的電子郵件地址。這通常表示對方持有寄給您的簡報副本，例如您曾轉寄出去的郵件。建議您確認曾與哪些人分享過簡報。",
  },
  resubUsedBody: {
    en: "It has already been used, or it's no longer valid. If you're still not receiving the brief, contact your T-Global representative.",
    zh: "此連結已被使用或已失效。如果您仍未收到簡報，請聯絡您的 T-Global 聯絡窗口。",
  },
  resubExpiredTitle: { en: "This link has expired", zh: "此連結已過期" },
  resubExpiredBody: {
    en: "Resubscribe links only work for a limited time after the unsubscribe. To get the brief again, contact your T-Global representative.",
    zh: "重新訂閱連結僅在取消訂閱後的一段時間內有效。如需重新收到簡報，請聯絡您的 T-Global 聯絡窗口。",
  },
};
export const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

// Pick a language variant. Tolerates a bare string or an { en, zh } object.
export const bi = (v, lang) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return v[lang] ?? v.en ?? v.zh ?? "";
};

export const formatDate = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(lang === "zh" ? "zh-TW" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ── in-app article preview ──────────────────────────────────────────────────
// Source links open in a right-side panel (iframe) instead of a new tab, so
// readers stay on our site. Falls back to window.open when no provider is
// mounted (e.g. a page that renders these components without wrapping them).
const ArticlePreviewContext = createContext({
  openArticle: (url) => window.open(url, "_blank", "noopener,noreferrer"),
});

// Intercept a plain click (but let cmd/ctrl/shift/middle-click behave like a
// normal link, so "open in new tab" still works from the browser itself). The
// side panel only exists at md+ (see ArticlePanel's `md:w-1/2`), so below that
// breakpoint just let the link open a new tab as it always has.
function previewClickHandler(openArticle, url, title, lang) {
  return (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    e.preventDefault();
    openArticle(url, title, lang);
  };
}

function ArticlePanel({ preview, onClose }) {
  const { url, title, lang } = preview;
  let domain = "";
  try {
    domain = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    // ignore malformed urls
  }

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
        onClick={onClose}
        aria-hidden
      />
      <div className="fixed inset-y-0 right-0 z-50 w-full md:w-1/2 bg-white dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-100 dark:border-gray-700/60">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {title || domain || url}
            </p>
            {domain && (
              <p className="text-xs text-gray-400 truncate">{domain}</p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-md font-semibold text-orange-600 dark:text-orange-400 hover:underline whitespace-nowrap"
            >
              {t("openInNewTab", lang)} ↗
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <p className="px-4 py-2 text-[13px] text-red-500 font-semibold border-b border-gray-100 dark:border-gray-700/60">
          {t("embedHint", lang)}
        </p>
        <iframe
          src={url}
          title={title || url}
          className="flex-1 w-full border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
        />
      </div>
    </>
  );
}

export function ArticlePreviewProvider({ children }) {
  const [preview, setPreview] = useState(null);
  const openArticle = useCallback((url, title, lang) => {
    if (!url) return;
    setPreview({ url, title, lang });
  }, []);
  const closeArticle = useCallback(() => setPreview(null), []);

  return (
    <ArticlePreviewContext.Provider value={{ openArticle }}>
      {children}
      {preview && <ArticlePanel preview={preview} onClose={closeArticle} />}
    </ArticlePreviewContext.Provider>
  );
}

// ── citations ────────────────────────────────────────────────────────────────
// Gemini leaves inline [[i]] / [[i,j]] markers in prose fields, referring back
// to the same indexed news list it was given (see gemini_client.py's
// _sanitize_citations). "sources" (payload.sources, keyed by string index) is
// how those indices resolve to a real title/url/domain for display.
const _CITATION_RE = /\[\[([\d,\s]+)\]\]/g;

// Rendered via a portal at position:fixed (see below) so it can never be
// clipped by an ancestor's overflow-hidden (report cards use that for their
// rounded corners). Width must stay in sync with the "w-64" class below.
const _CITATION_POPOVER_WIDTH = 256;
const _CITATION_POPOVER_GAP = 8;
// Closing is delayed rather than instant so moving the cursor from the marker
// into the popover (crossing the gap between them) doesn't dismiss it first.
const _CITATION_CLOSE_DELAY = 200;

function _citationPopoverPosition(rect) {
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - _CITATION_POPOVER_WIDTH / 2, 8),
    window.innerWidth - _CITATION_POPOVER_WIDTH - 8,
  );
  // Prefer opening above the marker; flip below when there isn't room.
  const placeAbove = rect.top > 200;
  return placeAbove
    ? {
        left,
        top: rect.top - _CITATION_POPOVER_GAP,
        transform: "translateY(-100%)",
      }
    : { left, top: rect.bottom + _CITATION_POPOVER_GAP, transform: "none" };
}

function CitationMark({ indices, sources, lang }) {
  const { openArticle } = useContext(ArticlePreviewContext);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const closeTimer = useRef(null);
  const refs = indices.map((i) => sources[i]).filter(Boolean);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(
      () => setOpen(false),
      _CITATION_CLOSE_DELAY,
    );
  };
  const show = () => {
    cancelClose();
    if (buttonRef.current) {
      setPos(
        _citationPopoverPosition(buttonRef.current.getBoundingClientRect()),
      );
    }
    setOpen(true);
  };

  // A fixed-position popover doesn't track the trigger while the page
  // scrolls/resizes underneath it, so just close it rather than let it drift.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  useEffect(() => () => cancelClose(), []);

  if (refs.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onClick={(e) => {
          e.stopPropagation();
          open ? setOpen(false) : show();
        }}
        className="align-super text-[10px] font-bold text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 mx-0.5 leading-none"
      >
        [{indices.map((i) => Number(i) + 1).join(",")}]
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              transform: pos.transform,
            }}
            className="z-50 w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-1.5 space-y-0.5"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {refs.map((src, i) => (
              <a
                key={i}
                href={src.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  setOpen(false);
                  previewClickHandler(openArticle, src.url, src.title, lang)(e);
                }}
                className="block rounded-md px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-gray-700/60"
              >
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 leading-snug line-clamp-2">
                  {src.title || src.url}
                </p>
                {src.source_domain && (
                  <p className="text-[11px] text-gray-400 truncate">
                    {src.source_domain}
                  </p>
                )}
              </a>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// Split text on [[i]] / [[i,j]] markers and interleave CitationMark components.
// Returns the plain string unchanged when there's nothing to cite.
export function withCitations(text, sources, lang) {
  if (!text || !sources || Object.keys(sources).length === 0) return text;
  _CITATION_RE.lastIndex = 0;
  if (!_CITATION_RE.test(text)) return text;
  _CITATION_RE.lastIndex = 0;

  const parts = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = _CITATION_RE.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const indices = m[1]
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    parts.push(
      <CitationMark
        key={`c${key++}`}
        indices={indices}
        sources={sources}
        lang={lang}
      />,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ── shared bits ──────────────────────────────────────────────────────────────
export function Bullets({ items, lang, dot = "bg-orange-400", sources }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="space-y-2">
      {items.map((b, i) => (
        <li
          key={i}
          className="flex gap-2 text-sm text-gray-700 dark:text-gray-200"
        >
          <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
          <span className="leading-relaxed">
            {withCitations(bi(b, lang), sources, lang)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function SectionShell({ title, lang, children }) {
  return (
    <section className="border-t border-gray-100 dark:border-gray-700/60 pt-5">
      {title && (
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
          {bi(title, lang)}
        </h3>
      )}
      {children}
    </section>
  );
}

// ── section renderers ────────────────────────────────────────────────────────
// Heading is fixed in code, never section.title: the label is a statement
// about who wrote this section, so it must not drift with the day's output.
// (It was also hardcoded English before, which broke the zh render.)
export function ExecutiveSummary({ section, lang, sources }) {
  return (
    <SectionShell title={t("aiSynthesis", lang)} lang={lang}>
      <div className="rounded-lg bg-slate-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 p-4 space-y-3">
        <Bullets items={section.bullets} lang={lang} sources={sources} />
        {section.overall_assessment && (
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed border-t border-gray-200/60 dark:border-gray-700/60 pt-3">
            {withCitations(bi(section.overall_assessment, lang), sources, lang)}
          </p>
        )}
      </div>
    </SectionShell>
  );
}

// ── funnel breakdown ─────────────────────────────────────────────────────────
// Each story's application -> device -> thermal_challenge -> ... ->
// engineering_technology chain comes straight from the backend's
// classification pass (see gemini_client.py's classify_results /
// _assemble_funnel_groups) - synthesis never re-derives these fields, so
// they're rendered as-is, not via bi()'s citation path. A "low" confidence
// field gets a visible "needs review" flag instead of being shown as if it
// were certain.
const FUNNEL_FIELD_ORDER = [
  "device",
  "thermal_challenge",
  "solution_architecture",
  "product",
  "material",
  "supply_chain",
  "engineering_technology",
];

const FUNNEL_FIELD_LABELS = {
  device: { en: "Application", zh: "應用領域" },
  thermal_challenge: { en: "Thermal challenge", zh: "散熱挑戰" },
  solution_architecture: { en: "Solution architecture", zh: "解決方案架構" },
  product: { en: "Product", zh: "產品" },
  material: { en: "Material", zh: "材料" },
  supply_chain: { en: "Supply chain", zh: "供應鏈" },
  engineering_technology: { en: "Engineering technology", zh: "工程技術" },
};

function FunnelFieldRow({ fieldKey, field, lang }) {
  const value = bi(field, lang);
  if (!value) return null;
  return (
    <div className="flex items-start gap-1.5 text-xs">
      <span className="shrink-0 font-semibold text-gray-400 dark:text-gray-500 w-28">
        {bi(FUNNEL_FIELD_LABELS[fieldKey], lang)}
      </span>
      <span className="text-gray-700 dark:text-gray-200 leading-relaxed">
        {value}
      </span>
      {field.confidence === "low" && (
        <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wide">
          {t("needsReview", lang)}
        </span>
      )}
    </div>
  );
}

function FunnelChain({ funnel, lang }) {
  if (!funnel) return null;
  const rows = FUNNEL_FIELD_ORDER.filter((k) => funnel[k]);
  if (rows.length === 0) return null;
  return (
    <div className="mt-1 rounded-lg bg-slate-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 p-3 space-y-1.5">
      {rows.map((k) => (
        <FunnelFieldRow key={k} fieldKey={k} field={funnel[k]} lang={lang} />
      ))}
    </div>
  );
}

export function FunnelNewsCard({ item, lang, sources }) {
  const { openArticle } = useContext(ArticlePreviewContext);
  const href = item.url;
  const headline = bi(item.headline, lang);
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: previewClickHandler(openArticle, href, headline, lang),
      }
    : {};
  return (
    <div className="flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 overflow-hidden">
      {item.image_url && (
        <Wrapper {...wrapperProps} className="block">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            className="h-40 w-full object-cover bg-gray-100 dark:bg-gray-700"
          />
        </Wrapper>
      )}
      <div className="flex flex-col gap-2 p-4">
        <Wrapper {...wrapperProps} className="block group">
          <h4 className="text-sm font-bold text-gray-900 dark:text-white leading-snug group-hover:text-orange-600 dark:group-hover:text-orange-400">
            {headline}
          </h4>
        </Wrapper>
        {(item.source_domain || item.published_at) && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {item.source_domain && (
              <span className="truncate">{item.source_domain}</span>
            )}
            {item.source_domain && item.published_at && (
              <span aria-hidden>·</span>
            )}
            {item.published_at && (
              <span className="shrink-0">
                {formatDate(item.published_at, lang)}
              </span>
            )}
          </div>
        )}
        {item.digest && (
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {withCitations(bi(item.digest, lang), sources, lang)}
          </p>
        )}
        {bi(item.why_it_matters, lang) && (
          <div className="mt-1 rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20 p-3">
            <h5 className="text-[11px] font-bold uppercase tracking-wide text-orange-600 dark:text-orange-400 mb-1">
              {t("whyItMatters", lang)}
            </h5>
            <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
              {withCitations(bi(item.why_it_matters, lang), sources, lang)}
            </p>
          </div>
        )}
        <FunnelChain funnel={item.funnel} lang={lang} />
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={previewClickHandler(openArticle, href, headline, lang)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-orange-600 dark:text-orange-400 hover:underline"
          >
            {t("readSource", lang)} ↗
          </a>
        )}
      </div>
    </div>
  );
}

export function FunnelBreakdown({ section, lang, sources }) {
  const groups = Array.isArray(section.groups) ? section.groups : [];
  if (groups.length === 0) return null;
  return (
    <SectionShell title={section.title} lang={lang}>
      <div className="space-y-6">
        {groups.map((g, gi) => {
          const groupItems = g.items || [];
          return (
            <div key={gi}>
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-orange-500"
                  aria-hidden
                />
                <h4 className="shrink-0 text-sm font-bold uppercase tracking-wide text-orange-600 dark:text-orange-400">
                  {bi(g.title, lang)}
                </h4>
                <span
                  className="h-px flex-1 bg-orange-200 dark:bg-orange-500/20"
                  aria-hidden
                />
                {groupItems.length > 0 && (
                  <span className="shrink-0 text-[11px] font-semibold text-gray-400">
                    {groupItems.length} {t("items", lang)}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {groupItems.map((it, i) => (
                  <FunnelNewsCard
                    key={i}
                    item={it}
                    lang={lang}
                    sources={sources}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

// ── priority spotlight ───────────────────────────────────────────────────────
// Editor-queued "must-cover" items (see backend gemini_client.py's
// _assemble_priority_spotlight, which guarantees one card per is_priority
// item regardless of what synthesis produced). Reuses FunnelNewsCard - same
// card shape (headline/digest/why_it_matters/funnel chain) - but in its own
// visually distinct banner rather than a plain SectionShell, so it reads as
// "someone put this here on purpose," not just another ranked story.
export function PrioritySpotlight({ section, lang, sources }) {
  const items = Array.isArray(section.items) ? section.items : [];
  if (items.length === 0) return null;
  return (
    <section className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/[0.06] p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg
          className="w-4 h-4 text-amber-500 shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 1.5l2.6 5.6 6.1.6-4.6 4.1 1.4 6-5.5-3.2-5.5 3.2 1.4-6-4.6-4.1 6.1-.6z" />
        </svg>
        <h3 className="text-sm font-bold text-amber-800 dark:text-amber-400">
          {bi(section.title, lang)}
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <FunnelNewsCard key={i} item={it} lang={lang} sources={sources} />
        ))}
      </div>
    </section>
  );
}

// Fallback for "custom"/unknown section types: render whatever prose/bullets exist.
export function GenericSection({ section, lang, sources }) {
  return (
    <SectionShell title={section.title} lang={lang}>
      {section.body && (
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
          {withCitations(bi(section.body, lang), sources, lang)}
        </p>
      )}
      <Bullets items={section.bullets} lang={lang} sources={sources} />
    </SectionShell>
  );
}

export function Section({ section, lang, sources }) {
  switch (section.type) {
    case "executive_summary":
      return (
        <ExecutiveSummary section={section} lang={lang} sources={sources} />
      );
    case "funnel_breakdown":
      return (
        <FunnelBreakdown section={section} lang={lang} sources={sources} />
      );
    case "priority_spotlight":
      return (
        <PrioritySpotlight section={section} lang={lang} sources={sources} />
      );
    default:
      return <GenericSection section={section} lang={lang} sources={sources} />;
  }
}

// ── All-news appendix ────────────────────────────────────────────────────────
function NewsRow({ item, lang, citationIndex }) {
  const { openArticle } = useContext(ArticlePreviewContext);
  const href = item.url;
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: previewClickHandler(openArticle, href, item.title, lang),
      }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="group relative flex gap-3 rounded-lg border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/40 p-2.5 hover:border-orange-200 dark:hover:border-orange-500/40 hover:shadow-sm transition-all"
    >
      {citationIndex != null && (
        <span
          title={`${t("citedAs", lang)} [${Number(citationIndex) + 1}]`}
          className="absolute -top-1.5 -left-1.5 inline-flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-[10px] font-bold h-4 min-w-[1rem] px-1 border border-white dark:border-gray-800"
        >
          {Number(citationIndex) + 1}
        </span>
      )}
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
          className="h-20 w-28 shrink-0 rounded-md object-cover bg-gray-100 dark:bg-gray-700"
        />
      ) : (
        <div className="h-20 w-28 shrink-0 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-gray-300 dark:text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 5h16v14H4V5zm3 3h5m-5 3h10m-10 3h7M15 8h2v3h-2z"
            />
          </svg>
        </div>
      )}
      <div className="min-w-0 flex flex-col py-0.5">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-snug line-clamp-2 group-hover:text-orange-600 dark:group-hover:text-orange-400">
          {item.title}
        </p>
        <div className="mt-auto flex items-center gap-2 text-xs text-gray-400 pt-1.5">
          {item.source_domain && (
            <span className="truncate font-medium text-gray-500 dark:text-gray-400">
              {item.source_domain}
            </span>
          )}
          {item.source_domain && item.published_at && (
            <span aria-hidden>·</span>
          )}
          {item.published_at && (
            <span className="shrink-0">
              {formatDate(item.published_at, lang)}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

// The full gathered-news list, grouped by application, so readers can open any
// single story (not just the ones featured in the funnel breakdown). Collapsed
// by default.
export function AllNews({ groups, lang, sources }) {
  const [open, setOpen] = useState(false);
  // "news" (this appendix) and "sources" (citation targets) are built from
  // separately-filtered lists on the backend, so they don't share index
  // positions — match rows to citation numbers by url instead.
  const urlToIndex = useMemo(() => {
    const map = {};
    if (sources) {
      for (const [idx, src] of Object.entries(sources)) {
        if (src?.url) map[src.url] = idx;
      }
    }
    return map;
  }, [sources]);
  if (!Array.isArray(groups) || groups.length === 0) return null;
  const total = groups.reduce((n, g) => n + (g.items?.length || 0), 0);
  return (
    <section className="border-t border-gray-100 dark:border-gray-700/60 pt-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 w-full text-left"
      >
        <svg
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">
          {t("allNews", lang)}
        </h3>
        <span className="text-xs text-gray-400">
          {total} {t("items", lang)}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-5">
          {groups.map((g) => (
            <div key={g.key}>
              <h4 className="text-md font-bold uppercase tracking-wide text-orange-600 mb-4">
                {bi(g.label, lang)}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {(g.items || []).map((it, i) => (
                  <NewsRow
                    key={i}
                    item={it}
                    lang={lang}
                    citationIndex={it.url ? urlToIndex[it.url] : undefined}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function StatPill({ value, label }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-lg font-bold text-gray-900 dark:text-white">
        {value}
      </span>
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );
}

// ── Public pages (public/) ──────────────────────────────────────────────────
// The frame for the unsubscribe / resubscribe pages. Reached from the email by
// people who may have no account, so light-only and outside the app chrome -
// styled after the Login page, the other screen an outsider sees.
export function PublicPageShell({ lang, onLang, children }) {
  return (
    <div className="min-h-screen bg-slate-100 font-sans flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 sm:p-8">
        <div className="flex justify-end mb-4">
          {[
            ["en", "English"],
            ["zh", "繁體中文"],
          ].map(([code, label]) => (
            <button
              key={code}
              type="button"
              onClick={() => onLang(code)}
              className={`text-sm ml-2 px-3 py-2 rounded-lg transition-all duration-200 font-medium ${
                lang === code
                  ? "bg-orange-600 text-white shadow-md"
                  : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-col items-center gap-2 mb-8">
          <img src="/logo_tglobal.png" alt="T-Global" className="w-36 h-auto" />
          <span className="text-xs uppercase tracking-wider text-gray-500">
            {t("pubBrand", lang)}
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
